import type { Config } from "vega-lite";
import type { VizPalette } from "./tokens.js";

/** A clean, modern UI sans stack that matches the app shell. */
export const DEFAULT_FONT =
  "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

export interface VizThemeOptions {
  /** Partially override the resolved palette (e.g. a custom categorical set). */
  palette?: Partial<VizPalette>;
  /** Font family applied to all text marks. */
  font?: string;
}

/**
 * Builds a Vega-Lite {@link Config} from a resolved palette. This is the heart
 * of "beautiful by default": muted hairline gridlines, no chart border box,
 * rounded bar tops, generous label spacing, and the design-system categorical
 * colors — all wired to the app's theme tokens.
 */
export function buildVegaConfig(
  palette: VizPalette,
  options?: VizThemeOptions,
): Config {
  const font = options?.font ?? DEFAULT_FONT;
  const {
    background,
    foreground,
    mutedForeground,
    border,
    primary,
    categorical,
  } = {
    ...palette,
    ...options?.palette,
  };
  const accent = categorical[0] ?? primary;

  return {
    background: "transparent",
    font,
    padding: 8,
    view: {
      stroke: null,
      continuousWidth: 320,
      continuousHeight: 280,
    },
    axis: {
      labelColor: mutedForeground,
      labelFont: font,
      labelFontSize: 11,
      labelPadding: 6,
      titleColor: foreground,
      titleFont: font,
      titleFontSize: 12,
      titleFontWeight: 600,
      titlePadding: 10,
      domainColor: border,
      domainWidth: 1,
      tickColor: border,
      tickSize: 5,
      gridColor: border,
      gridWidth: 1,
    },
    axisX: {
      grid: false,
      domain: true,
      ticks: true,
      labelAngle: 0,
    },
    axisY: {
      grid: true,
      domain: false,
      ticks: false,
      tickCount: 5,
    },
    legend: {
      labelColor: mutedForeground,
      labelFont: font,
      labelFontSize: 11,
      titleColor: foreground,
      titleFont: font,
      titleFontSize: 12,
      titleFontWeight: 600,
      symbolType: "circle",
      symbolSize: 80,
      orient: "right",
      offset: 16,
    },
    title: {
      color: foreground,
      font,
      fontSize: 15,
      fontWeight: 600,
      subtitleColor: mutedForeground,
      subtitleFont: font,
      subtitleFontSize: 12,
      anchor: "start",
      offset: 14,
    },
    range: {
      category: categorical,
    },
    mark: { color: accent, font },
    bar: { cornerRadiusEnd: 4, color: accent },
    line: { strokeWidth: 2.5, color: accent },
    point: { filled: true, size: 70, color: accent },
    area: { line: true, opacity: 0.25, color: accent },
    arc: { stroke: background, strokeWidth: 1.5 },
    rule: { color: border },
    text: { color: foreground, font, fontSize: 11 },
    numberFormat: ",.0f",
  };
}

type Plain = Record<string, unknown>;

function isPlainObject(value: unknown): value is Plain {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * Deep-merges two Vega-Lite configs, with `override` winning. Used to let a
 * caller's `spec.config` refine the themed defaults without losing them.
 */
export function mergeConfig(base: Config, override?: Config): Config {
  if (!override) return base;
  const out: Plain = { ...(base as Plain) };
  for (const [key, value] of Object.entries(override as Plain)) {
    const existing = out[key];
    if (isPlainObject(existing) && isPlainObject(value)) {
      out[key] = mergeConfig(existing as Config, value as Config);
    } else if (value !== undefined) {
      out[key] = value;
    }
  }
  return out as Config;
}
